"use client";
import Image from "next/image";
import FormPhoneAreaCode from "@/components/blocks/Form/FormPhoneAreaCode";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import Input, { InputProps } from "@/components/ui/input";

import RouterLink from "@/components/ui/router-link";

import { ValidateFormItem } from "@/components/ui/validate";

import { useScopedI18n } from "@/messages/client";

import {
  Building2,
  CircleAlert,
  ContactRound,
  Mail,
  Smartphone,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { match } from "ts-pattern";
// import generateUsername from "@/lib/generateUsername";
import { RegisterParams } from "@/server/type";
import FormLocation from "@/components/blocks/Form/FormLocation";
import { FormField } from "@/schemas/type";
import { postEmailCode, postRegister } from "@/server/user";

import useSWRMutation from "swr/mutation";
import useCountDown from "@/hooks/useCountDown";
import toast from "react-hot-toast";
import { useCodeMessage } from "@/hooks/useCodeMessage";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { CodeSMSInput } from "./Login";
import { cn } from "@/lib/utils";
import useLocaleRouter from "@/hooks/useLocaleRouter";

interface FormState extends RegisterParams {
  privacy?: boolean;
  confirmPassword?: string;
}

// function UserNameInput(props: {
//   onChange?: (value: string) => void;
//   value?: string;
//   required?: boolean;
// }) {
//   const t = useScopedI18n("注册模块");

//   return (
//     <Input
//       required={props.required}
//       value={props.value}
//       onChange={(e) => props.onChange?.(e.target.value)}
//       placeholder={t("账号名称")}
//       suffix={
//         <div
//           className="cursor-pointer whitespace-nowrap pl-2 text-xs text-[#2967E3] transition-opacity hover:opacity-80"
//           onClick={() => {
//             const username = generateUsername();
//             props.onChange?.(username ?? "");
//           }}>
//           {t("自动生成")}
//         </div>
//       }
//     />
//   );
// }

export function CitySelectForm(props: {
  className?: string;
  required?: boolean;
  field?: FormField;
}) {
  const t = useScopedI18n("注册模块");

  const { control, setValue } = useFormContext<FormState>();

  const location = useWatch({
    control,
    name: "location",
  });

  if (location !== undefined && location !== "1") {
    return null;
  }

  return (
    <FormLocation
      className={props.className}
      field={props.field}
      isFetching={location !== undefined}
      level="2"
      parent={location ?? ""}
      placeholder={t("城市")}
      required={props.required}
      onLabelChange={(label) => {
        setValue("cityName", label);
      }}
    />
  );
}

function CodeEmailInput(props: InputProps) {
  const t = useScopedI18n("注册模块");

  const locale = useCurrentLocale();

  const { control, trigger: formTrigger } = useFormContext<FormState>();

  const email = useWatch({
    control,
    name: "email",
  });

  const { count, start, isLoading, clear } = useCountDown(60);

  const { isMutating, trigger } = useSWRMutation(
    email ? `/api/email/code/${email}` : null,
    () => {
      if (!email) return;

      return postEmailCode({
        email,
        language: locale === "cn" ? "zh" : locale,
      });
    },
    {
      onSuccess: (res) => {
        if (res && "data" in res && res.data === "success") {
          start();

          toast.success(t("验证码已发送至邮箱"));
        }
      },
      onError: () => {
        clear();
        toast.error(t("验证码发送失败"));
      },
    }
  );

  return (
    <Input
      {...props}
      placeholder={t("请输入验证码")}
      suffix={
        <Button
          variant="link"
          type="button"
          loading={isMutating}
          disabled={isLoading}
          className="cursor-pointer whitespace-nowrap pl-2 pr-0 text-xs text-[#2967E3] transition-opacity hover:opacity-80"
          onClick={() => {
            if (!email) {
              formTrigger("email");

              return;
            }

            trigger();
          }}>
          {isLoading
            ? t<any, any>("重新发送", {
                count: count ?? 0,
              })
            : t("获取验证码")}
        </Button>
      }
    />
  );
}

export function PrivacyCheckbox(props: {
  onChange?: (checked: boolean) => void;
  value?: boolean;
  className?: string;
}) {
  const t = useScopedI18n("注册模块");

  return (
    <label
      htmlFor="privacy"
      className={cn("flex items-center space-x-2 text-xs", props.className)}>
      <Checkbox
        id="privacy"
        checked={props.value}
        onCheckedChange={props.onChange}
      />
      <div className="flex items-center before:text-[#CC2016] before:content-['*']">
        {t<any, any>("注册协议勾选描述", {
          privacy: (
            <RouterLink
              href="/privacy-policy"
              target="_blank"
              className="text-[#2967E3]">
              {t("隐私声明")}
            </RouterLink>
          ),
        })}
      </div>
    </label>
  );
}

export default function Register() {
  const t = useScopedI18n("注册模块");

  const toastId = useRef<string>();

  const timer = useRef<NodeJS.Timeout>();

  const [step, setStep] = useState<"register" | "success">("register");

  const [verify, setVerify] = useState<"email" | "phone">("email");

  const router = useLocaleRouter();

  const { trigger, isMutating } = useSWRMutation(
    "/api/user/register",
    (_: string, { arg }: { arg: FormState }) => {
      return postRegister(arg);
    },
    {
      onError: () => {
        toast.error(t("注册失败描述"), {
          id: toastId.current,
        });
      },
    }
  );

  const methods = useForm<FormState>({
    defaultValues: {
      phoneArea: "86",
      privacy: false,
    },
  });

  const { handleSubmit, control, setValue } = methods;

  const codeMessage = useCodeMessage();

  const onSubmit = handleSubmit(async (data) => {
    try {
      toastId.current = toast.loading(t("提交请求中"));

      // 过滤空字符串
      Object.keys(data).forEach((key) => {
        if (!data[key as keyof FormState]) {
          delete data[key as keyof FormState];
        }
      });

      if ("privacy" in data) {
        delete data.privacy;
      }

      const res = await trigger(data);

      if ("errors" in res) {
        toast.error(
          codeMessage(res.errors[0].extensions.code, res.errors[0].message),
          {
            id: toastId.current,
          }
        );

        return;
      }

      if (res?.data === "success") {
        toast.success(t("注册成功提示"), {
          id: toastId.current,
        });

        setStep("success");
      }
    } catch (error) {
      toast.error(t("注册失败描述"), {
        id: toastId.current,
      });
    }
  });

  useEffect(() => {
    if (step === "success") {
      timer.current = setTimeout(() => {
        router.replace("/login");
      }, 3000);

      return () => {
        if (timer.current) {
          clearTimeout(timer.current);
        }
      };
    }
  }, [router, step]);

  return (
    <div className="flex min-h-[calc(100vh-2.75rem)] flex-col md:min-h-[calc(100vh-7.125rem)]">
      <div className="relative flex flex-1 bg-[url('/login-bg.webp')] bg-cover bg-no-repeat">
        <div className="register_container absolute inset-0 z-10"></div>

        <div className="relative z-20 w-full overflow-y-auto bg-white pb-6 md:bg-transparent md:pb-16 md:pt-8">
          <div className="content_container h-full w-full">
            <div className="h-full bg-white pb-10 md:mx-12 md:rounded-xl md:shadow-lg lg:mx-20">
              <div className="flex flex-col justify-between border-[#E5E5E5] pt-6 md:h-[4.375rem] md:flex-row md:items-center md:border-b md:px-8 md:pt-0">
                <h1 className="text-2xl font-medium text-[#CC2016]">
                  {t("注册标题")}
                </h1>

                <div className="flex items-center pt-3 text-sm md:pt-0">
                  {t<any, any>("登录链接", {
                    link: (
                      <RouterLink href="/login" className="text-[#2967E3]">
                        {t("去登录")}
                      </RouterLink>
                    ),
                  })}
                </div>
              </div>

              {match(step)
                .with("register", () => (
                  <FormProvider {...methods}>
                    <form
                      className="mx-auto w-full md:w-4/5 lg:w-1/2"
                      onSubmit={onSubmit}>
                      <h2 className="mt-5 py-5 text-lg font-medium md:mt-0 md:text-xl lg:text-2xl">
                        {t("注册信息")}
                      </h2>

                      <div className="space-y-4">
                        <Controller
                          name="firstName"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入姓名"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="firstName">
                              <Input
                                {...field}
                                required
                                suffix={<User color="#C2C6CE" />}
                                placeholder={t("姓名")}
                              />
                            </ValidateFormItem>
                          )}
                        />

                        <Controller
                          name="organization"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入公司"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="organization">
                              <Input
                                {...field}
                                required
                                suffix={<Building2 color="#C2C6CE" />}
                                placeholder={t("公司")}
                              />
                            </ValidateFormItem>
                          )}
                        />

                        <Controller
                          name="position"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入职位"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="position">
                              <Input
                                {...field}
                                required
                                suffix={<ContactRound color="#C2C6CE" />}
                                placeholder={t("职位")}
                              />
                            </ValidateFormItem>
                          )}
                        />

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <Controller
                            name="location"
                            control={control}
                            render={({ field }) => (
                              <FormLocation
                                field={field}
                                isFetching
                                level="1"
                                placeholder={t("国家")}
                                onLabelChange={(label) => {
                                  setValue("locationName", label);
                                  setValue("cityName", "");
                                  setValue("city", "");
                                }}
                              />
                            )}
                          />

                          <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                              <CitySelectForm field={field} />
                            )}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pb-5 pt-10">
                        <h2 className="text-lg font-medium md:text-xl lg:text-2xl">
                          {t("账号注册")}
                        </h2>

                        {/* <div className="text-sm text-[#a6a6a6]">
                          {match(verify)
                            .with("email", () => (
                              <div
                                className="flex cursor-pointer items-center space-x-2"
                                onClick={() => {
                                  setVerify("phone");

                                  setValue("email", "");
                                  setValue("verificationCode", "");
                                }}>
                                <Smartphone size={18} />

                                <span>{t("手机注册")}</span>
                              </div>
                            ))
                            .with("phone", () => (
                              <div
                                className="flex cursor-pointer items-center space-x-2"
                                onClick={() => {
                                  setVerify("email");

                                  setValue("phoneNumber", "");

                                  setValue("verificationCode", "");
                                }}>
                                <Mail size={18} />

                                <span>{t("邮箱注册")}</span>
                              </div>
                            ))
                            .otherwise(() => null)}
                        </div> */}
                      </div>

                      <div className="space-y-4">
                        <Controller
                          name="email"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入邮箱地址"),
                            },
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: t("请输入有效的邮箱地址"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="email">
                              <Input
                                {...field}
                                required
                                type="email"
                                placeholder={t("邮箱地址")}
                              />
                            </ValidateFormItem>
                          )}
                        />

                        <div className="flex space-x-2 text-xs text-[#808080]">
                          <div className="flex-shrink-0">
                            <CircleAlert size={16} color="#2967E3" />
                          </div>

                          <span>{t("邮箱提示")}</span>
                        </div>

                        <Controller
                          name="verificationCode"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入邮箱验证码"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="verificationCode">
                              <CodeEmailInput
                                value={field.value}
                                onChange={field.onChange}
                                required
                                placeholder={t("邮箱验证码")}
                              />
                            </ValidateFormItem>
                          )}
                        />

                        <Controller
                          name="phoneNumber"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入手机号"),
                            },
                            pattern: {
                              value: /^[1-9]\d{1,14}$/,
                              message: t("请输入有效的手机号"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="phoneNumber">
                              <Input
                                {...field}
                                type="tel"
                                autoComplete="off"
                                required
                                placeholder={t("手机号")}
                                prefixs={
                                  <Controller
                                    name="phoneArea"
                                    control={control}
                                    render={({ field }) => (
                                      <FormPhoneAreaCode
                                        value={field.value}
                                        onChange={field.onChange}
                                      />
                                    )}
                                  />
                                }
                              />
                            </ValidateFormItem>
                          )}
                        />

                        {/* {match(verify)
                          .with("email", () => (
                            <>
                              <Controller
                                name="email"
                                control={control}
                                rules={{
                                  required: {
                                    value: true,
                                    message: t("请输入邮箱地址"),
                                  },
                                  pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: t("请输入有效的邮箱地址"),
                                  },
                                }}
                                render={({ field }) => (
                                  <ValidateFormItem name="email">
                                    <Input
                                      {...field}
                                      required
                                      type="email"
                                      placeholder={t("邮箱地址")}
                                    />
                                  </ValidateFormItem>
                                )}
                              />

                              <div className="flex space-x-2 text-xs text-[#808080]">
                                <div className="flex-shrink-0">
                                  <CircleAlert size={16} color="#2967E3" />
                                </div>

                                <span>{t("邮箱提示")}</span>
                              </div>

                              <Controller
                                name="verificationCode"
                                control={control}
                                rules={{
                                  required: {
                                    value: true,
                                    message: t("请输入邮箱验证码"),
                                  },
                                }}
                                render={({ field }) => (
                                  <ValidateFormItem name="verificationCode">
                                    <CodeEmailInput
                                      value={field.value}
                                      onChange={field.onChange}
                                      required
                                      placeholder={t("邮箱验证码")}
                                    />
                                  </ValidateFormItem>
                                )}
                              />
                            </>
                          ))
                          .with("phone", () => (
                            <>
                              <Controller
                                name="phoneNumber"
                                control={control}
                                rules={{
                                  required: {
                                    value: true,
                                    message: t("请输入手机号"),
                                  },
                                  pattern: {
                                    value: /^[1-9]\d{1,14}$/,
                                    message: t("请输入有效的手机号"),
                                  },
                                }}
                                render={({ field }) => (
                                  <ValidateFormItem name="phoneNumber">
                                    <Input
                                      {...field}
                                      type="tel"
                                      autoComplete="off"
                                      placeholder={t("手机号")}
                                      prefixs={
                                        <Controller
                                          name="phoneArea"
                                          control={control}
                                          render={({ field }) => (
                                            <FormPhoneAreaCode
                                              value={field.value}
                                              onChange={field.onChange}
                                            />
                                          )}
                                        />
                                      }
                                    />
                                  </ValidateFormItem>
                                )}
                              />

                              <Controller
                                name="verificationCode"
                                control={control}
                                rules={{
                                  required: {
                                    value: true,
                                    message: t("请输入邮箱验证码"),
                                  },
                                }}
                                render={({ field }) => (
                                  <ValidateFormItem name="verificationCode">
                                    <CodeSMSInput {...field} />
                                  </ValidateFormItem>
                                )}
                              />
                            </>
                          ))
                          .otherwise(() => null)} */}

                        {/* <Controller
                          name="account"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入账号名称"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="account">
                              <UserNameInput
                                value={field.value}
                                onChange={field.onChange}
                                required
                              />
                            </ValidateFormItem>
                          )}
                        /> */}

                        <Controller
                          name="password"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入密码"),
                            },
                            maxLength: {
                              value: 60,
                              message: t("密码长度不能超过60位"),
                            },
                            pattern: {
                              // 密码校验规则，至少8位，必须包含大小写字母、数字和特殊字符
                              value:
                                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&])[A-Za-z\d$@$!%*?&]{8,}/,

                              message: t("密码格式规则"),
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="password">
                              <Input
                                {...field}
                                required
                                type="password"
                                placeholder={t("密码")}
                              />
                            </ValidateFormItem>
                          )}
                        />

                        <Controller
                          name="confirmPassword"
                          control={control}
                          rules={{
                            required: {
                              value: true,
                              message: t("请输入确认密码"),
                            },
                            validate: (value) => {
                              if (value !== methods.getValues("password")) {
                                return t("两次密码不一致");
                              }
                            },
                          }}
                          render={({ field }) => (
                            <ValidateFormItem name="confirmPassword">
                              <Input
                                {...field}
                                type="password"
                                required
                                placeholder={t("确认密码")}
                              />
                            </ValidateFormItem>
                          )}
                        />
                      </div>

                      <Controller
                        name="privacy"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: t("请阅读并勾选"),
                          },
                        }}
                        render={({ field }) => (
                          <ValidateFormItem className="mt-12" name="privacy">
                            <PrivacyCheckbox
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </ValidateFormItem>
                        )}
                      />

                      <Button
                        className="mt-6 w-full rounded"
                        type="submit"
                        loading={isMutating}>
                        {t("注册")}
                      </Button>
                    </form>
                  </FormProvider>
                ))
                .with("success", () => (
                  <div className="flex flex-col items-center justify-center">
                    <h2 className="pb-10 pt-10 text-3xl font-medium text-[#B5372E] md:pb-0 md:pt-16">
                      {t("注册成功提示")}
                    </h2>

                    <Image
                      className="hidden md:block"
                      src="/register-success.webp"
                      alt="Register Success"
                      width={422}
                      height={486}
                    />

                    <Image
                      className="block md:hidden"
                      src="/register-success-sm.webp"
                      alt="Register Success"
                      width={290}
                      height={222}
                    />
                  </div>
                ))
                .otherwise(() => null)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
